import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import Header from "./components/Header";
import LoadingScreen from "./components/LoadingScreen";

import FormJourney from "./FormJourney";
import apiConnector from "./apiConnector";

import useIsMounted from "./utils/useIsMounted";
import useAmplitude from "./utils/useAmplitude";
import useOnboarding from "./utils/useOnboarding";
import ThemeContext from "./utils/theme";
import { StepShape, ThemeShape } from "./utils/types";

import "./App.scss";

const JOURNEY_IDENTITY_KEY = "ph-onboarding";

export default function App({ theme, amplitudeConfig, brandName, logo }) {
  const [loading, setLoading] = useState(true);
  const { onboarding, setInitialOnboarding, nextStep, currentPosition } = useOnboarding();
  const [invertHeader, setInvertHeader] = useState(false);
  const api = useMemo(apiConnector, []);
  const isMounted = useIsMounted();
  const amplitudeInstance = useAmplitude({
    config: amplitudeConfig,
  });

  useEffect(() => {
    if (onboarding.id !== undefined) {
      amplitudeInstance.identify(JOURNEY_IDENTITY_KEY, onboarding.id);
    }
  }, [amplitudeInstance, onboarding.id]);

  useEffect(() => {
    if (onboarding.currentStep?.type === "landing") {
      setInvertHeader(true);
    } else {
      setInvertHeader(false);
    }
  }, [onboarding.currentStep]);

  useEffect(() => {
    const fetchOnboarding = async () => {
      const { body } = await api.getInitialJourney();
      const newJourney = body.onboarding;

      if (isMounted()) {
        const { id, steps, journey, finalURL, variables } = newJourney;

        setInitialOnboarding({
          id,
          steps,
          journey,
          finalURL,
          variables,
        });
        setLoading(false);
      }
    };

    if (isMounted()) {
      fetchOnboarding();
    }
  }, [api, isMounted, setInitialOnboarding]);

  const handleEveryStepChange = (value, id) => {
    const eventProperties =
      typeof value === "string"
        ? {
            value,
            stepOrder: currentPosition,
          }
        : {
            stepOrder: currentPosition,
          };

    amplitudeInstance.logEvent(id, eventProperties);

    setLoading(true);

    return api
      .stepTouch({
        value,
        id,
      })
      .then((response) => {
        if (isMounted()) {
          setLoading(false);
          nextStep(value);
        }

        return response;
      });
  };

  const { currentStep, variables: allVariables } = onboarding;

  return (
    <ThemeContext.Provider value={theme}>
      {loading && <LoadingScreen />}

      <Header
        title={brandName}
        logo={logo}
        invert={invertHeader}
        stepConf={{
          max: onboarding.journey.length,
          current: 0,
        }}
      />

      {currentStep && (
        <FormJourney
          step={currentStep}
          onStepChange={handleEveryStepChange}
          emptyVariableFormatter={customVariableFormatter}
          variables={allVariables}
        />
      )}
    </ThemeContext.Provider>
  );
}

function customVariableFormatter(content, variable) {
  return content?.replace(`{{${variable?.name}}}`, "");
}

App.propTypes = {
  steps: PropTypes.arrayOf(StepShape),
  brandName: PropTypes.string.isRequired,
  logo: PropTypes.string.isRequired,
  theme: ThemeShape.isRequired,
  amplitudeConfig: PropTypes.object,
};
